import {
  Box,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BgBox from "../../common/BgBox";
import MobileAppImage from "../../assets/images/mobile_app_image.png";
import MccLogo from "../../assets/images/mcc-logo.png";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import GooglePlayImage from "../../assets/images/google_play_image.png";
import { useDispatch } from "react-redux";
import { addScanCodeFunc } from "../../actions/Agency";
import BiosynciqFooter from "../../components/BiosynciqFooter";
import { AppStoreImage } from "../../assets/constants";
import CommonStoreButton from "../../components/CommonStoreButton";
import CircularProgressLoading from "../../common/CircularProgress";
import {
  buildEnrollmentAppUrl,
  buildEnrollmentQuery,
  buildPracticeTokenAppUrl,
  isMobileDevice,
  resolveEnrollmentToken,
  resolvePracticeToken,
} from "../../utils/helpers/enrollmentToken";

const AgencyQrCode = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const navigate = useNavigate();

  const enrollmentToken = searchParams.get("enrollment_token");
  const practiceToken = searchParams.get("token");
  const legacyAgencyId = searchParams.get("agency_id");
  const legacyAgencyName = searchParams.get("agency_name");
  const isMobile = isMobileDevice();

  const [agencyId, setAgencyId] = useState(legacyAgencyId);
  const [agencyName, setAgencyName] = useState(legacyAgencyName);
  const [loading, setLoading] = useState(
    Boolean(enrollmentToken || practiceToken),
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enrollmentToken && !practiceToken) return;

    let cancelled = false;

    const resolveLink = enrollmentToken
      ? resolveEnrollmentToken(enrollmentToken).then(
          ({ agencyId: id, agencyName: name }) => ({
            agencyId: id,
            agencyName: name,
            appUrl: buildEnrollmentAppUrl(enrollmentToken),
          }),
        )
      : resolvePracticeToken(practiceToken).then(
          ({ agencyId: id, agencyName: name }) => ({
            agencyId: id,
            agencyName: name,
            appUrl: buildPracticeTokenAppUrl(practiceToken),
          }),
        );

    resolveLink
      .then(({ agencyId: id, agencyName: name, appUrl }) => {
        if (cancelled) return;
        setAgencyId(String(id));
        setAgencyName(name);
        setError(null);
        setLoading(false);

        // Only open the app after the link token is confirmed valid.
        // If the app is not installed, stay on this download landing page.
        if (isMobile) {
          window.location.href = appUrl;
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enrollmentToken, practiceToken, isMobile]);

  const practiceDetailsHandler = () => {
    if (enrollmentToken) {
      navigate(
        `/patient-details?${buildEnrollmentQuery({
          enrollmentToken,
          agencyName,
        })}`,
      );
      return;
    }

    navigate(
      `/patient-details?agency_id=${agencyId}&agency_name=${encodeURIComponent(agencyName || "")}`,
    );
  };

  if (loading) {
    return (
      <BgBox>
        <Stack
          flex={1}
          alignItems="center"
          justifyContent="center"
          gap="16px"
          padding="24px"
        >
          <CircularProgressLoading />
          <Typography color="secondary" align="center">
            {enrollmentToken
              ? "Checking enrollment link..."
              : "Checking link..."}
          </Typography>
        </Stack>
      </BgBox>
    );
  }

  if (error) {
    return (
      <BgBox>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flex={1}
          padding="24px"
        >
          <Typography color="error" align="center">
            {error}
          </Typography>
        </Box>
        <BiosynciqFooter />
      </BgBox>
    );
  }

  return (
    <BgBox>
      <Box width="83px" height="80px">
        <img src={MccLogo} alt="mcc-logo" width="100%" height="100%" />
      </Box>

      <Box
        component="main"
        bgcolor="#fff"
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="32px"
        height="100%"
        width="100%"
        boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.08)"
        borderRadius="16px"
        padding="92px 24px"
        sx={{
          [theme.breakpoints.down("md")]: {
            padding: "24px 24px",
            flexDirection: "column",
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="32px"
        >
          <Box
            width="408px"
            height="437px"
            sx={{
              [theme.breakpoints.down("md")]: {
                height: "388px",
              },
              [theme.breakpoints.down("sm")]: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            <Box
              component="img"
              width="100%"
              sx={{
                [theme.breakpoints.down("sm")]: {
                  width: "85%",
                },
              }}
              height="100%"
              src={MobileAppImage}
              alt="BiosyncIQ app"
            />
          </Box>
        </Box>

        <Stack gap="16px">
          <Typography
            variant="h1"
            color="#124273"
            fontSize="4.5rem"
            sx={{
              [theme.breakpoints.down("sm")]: {
                fontSize: "38px",
              },
            }}
          >
            {agencyName}
          </Typography>
          <Typography
            variant="h5"
            color="rgba(0, 0, 0, 0.87)"
            fontSize="2rem"
            fontWeight={500}
          >
            Follow the steps to get registered with us{" "}
          </Typography>
          <Stack padding="24px 0" gap="8px">
            <Timeline
              sx={{
                padding: "6px 0",
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0,
                },
              }}
            >
              <TimelineItem>
                <TimelineSeparator>
                  <Box
                    width="24px"
                    height="24px"
                    borderRadius="50%"
                    bgcolor="#0C80F3"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="caption" color="#fff">
                      1
                    </Typography>
                  </Box>
                  <TimelineConnector sx={{ margin: "4px 0", width: "1px" }} />
                </TimelineSeparator>
                <TimelineContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "0 8px",
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Download and install our mobile application on your phone.
                  </Typography>
                  <CommonStoreButton />
                </TimelineContent>
              </TimelineItem>

              <TimelineItem>
                <TimelineSeparator>
                  <Box
                    width="24px"
                    height="24px"
                    borderRadius="50%"
                    bgcolor="#0C80F3"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="caption" color="#fff">
                      2
                    </Typography>
                  </Box>
                </TimelineSeparator>
                <TimelineContent
                  sx={{
                    padding: "0 8px",
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Signup using our Practice ID {agencyId}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            </Timeline>
            {/* <Typography variant="body1" fontWeight={400}>
              or fill your details{" "}
              <Typography
                sx={{ textDecoration: "underline", cursor: "pointer" }}
                component="span"
                color="#0C80F3"
                onClick={practiceDetailsHandler}
              >
                {" "}
                here
              </Typography>{" "}
              and directly login to the app.
            </Typography> */}
          </Stack>
        </Stack>
      </Box>

      <BiosynciqFooter />
    </BgBox>
  );
};

export default AgencyQrCode;
